/**
 * 服务方工作台外壳与路由。
 *
 * ══ 为什么是一个**独立应用**，而不是员工控制台里的一个角色入口 ══
 *
 * 服务方是**外部机构**（机构排班员、检测机构对接人），与联合体内部员工不是一类身份：
 *   · 身份来源不同：员工来自 `staff` + 角色 + 守护关系；服务方来自「绑定的已准入主体」；
 *   · 权限模型不同：员工有 16 个权限点与可见范围；服务方**没有任何员工权限点**；
 *   · 凭证受众不同：`aud: 'staff'` vs `aud: 'provider'`，互不通用。
 *
 * 把外部界面打进内部员工控制台的同一个产物，迟早会把内部路由与内部代码一起送到外部方手里
 * （一个 bundle 里的路由表、接口路径、字段名全都可读）。因此本应用**独立构建、独立端口、
 * 独立 nginx 站点**，与 `apps/admin` 不共享任何构建产物，只共享设计令牌。
 *
 * ══ 三层守卫 ══
 *   ① 未登录 → 一律回 /login（服务方 token 与成员、员工 token 受众隔离）；
 *   ② 会话被服务端吊销（停用账号 / 主体准入失效）→ 401 时自动回登录页；
 *   ③ 越权访问别人的工单 → 接口返回 **404**（不是 403），页面照实显示，不泄露 id 是否存在。
 */
import { useCallback, useEffect, useState } from 'react';
import { Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { getToken, providerApi, setToken, setUnauthorizedHandler } from './lib/api.ts';
import type { ProviderSession } from './lib/api.ts';
import { admissionStatusText, formatNumber, formatText } from './lib/format.ts';
import { Badge } from './components/ui.tsx';
import { LoginPage } from './pages/LoginPage.tsx';
import { OverviewPage } from './pages/OverviewPage.tsx';
import { BookingsPage } from './pages/BookingsPage.tsx';
import { BookingDetailPage } from './pages/BookingDetailPage.tsx';

/**
 * 导航清单。
 *
 * **刻意只有两项**：服务方工作台不做「成员检索」也不做「导出」——
 * 接口层面不存在这两个能力，界面因此不设入口（多一个入口就等于承诺一个不存在的能力）。
 */
const NAV_ITEMS: { path: string; label: string }[] = [
  { path: '/', label: '工作台概览' },
  { path: '/bookings', label: '工单与履约' },
];

function Spinner() {
  return (
    <div className="state-box">
      <div className="spinner" />
      <div>正在校验服务方登录状态…</div>
    </div>
  );
}

function ProviderShell({ session, onLogout }: { session: ProviderSession; onLogout: () => void }) {
  return (
    <div className="app">
      <aside className="sidebar">
        <div>
          <div className="brand-mark">HEW³</div>
          <div className="brand-sub">服务方工作台</div>
          <div className="brand-tag">PROVIDER CONSOLE · 外部机构专用</div>
        </div>

        <nav className="side-nav">
          <div className="side-group">导航</div>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) => `nav-item${isActive ? ' on' : ''}`}
            >
              <span className="dot" />
              <span>{item.label}</span>
            </NavLink>
          ))}
          <div className="side-group" style={{ marginTop: 16 }}>
            边界
          </div>
          <div className="perm-block" style={{ margin: '0 4px', fontSize: 11 }}>
            默认只下发<b>履约必需信息</b>；健康 / 指标 / 报告 / 财富数据需成员单独授权。
            不提供按成员检索与导出。
          </div>
        </nav>

        <div className="side-foot">
          <div className="who-name">{formatText(session.provider.name)}</div>
          <div className="who-meta">
            联系人 {formatText(session.provider.contactName)} · {formatText(session.provider.phone)}
          </div>
          <div className="who-meta">绑定主体：{formatText(session.subject.name)}</div>
          <div className="sp8" />
          <button type="button" className="btn sm block" onClick={onLogout}>
            退出登录
          </button>
        </div>
      </aside>

      <div className="content">
        <div className="topbar">
          <div className="tb-title">
            HEW³ 服务方工作台
            <small>PROVIDER CONSOLE</small>
          </div>
          <div className="tb-meta">
            <Badge tone={session.subject.bookable ? 'ok' : 'danger'}>
              主体准入：{admissionStatusText(session.subject.admissionStatus)}
            </Badge>
            <Badge tone="e">可约资源 {formatNumber(session.resources.bookable)} 项</Badge>
            <Badge tone="ghost">无内部员工权限</Badge>
          </div>
        </div>
        <div className="body">
          <Routes>
            <Route path="/" element={<OverviewPage session={session} />} />
            <Route path="/bookings" element={<BookingsPage />} />
            <Route path="/bookings/:id" element={<BookingDetailPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  // undefined = 还在校验；null = 未登录
  const [session, setSession] = useState<ProviderSession | null | undefined>(undefined);

  useEffect(() => {
    // token 失效（服务端吊销 / 过期 / 主体准入失效）时自动退回登录页
    setUnauthorizedHandler(() => setSession(null));
    void (async () => {
      if (!getToken()) {
        setSession(null);
        return;
      }
      try {
        setSession(await providerApi.me());
      } catch {
        // 401 已在 api 层清掉 token；其余错误同样回登录页，避免停在空壳里
        setSession(null);
      }
    })();
  }, []);

  const handleLoggedIn = useCallback(async (token: string) => {
    setToken(token);
    try {
      setSession(await providerApi.me());
    } catch {
      setToken(null);
      setSession(null);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await providerApi.logout();
    } catch {
      // 服务端吊销失败也要清本地登录态：不能因为网络问题把人留在工作台里
    }
    setToken(null);
    setSession(null);
  }, []);

  if (session === undefined) return <Spinner />;

  return (
    <Routes>
      <Route path="/login" element={session ? <Navigate to="/" replace /> : <LoginPage onLoggedIn={handleLoggedIn} />} />
      <Route
        path="/*"
        element={
          session ? (
            <ProviderShell session={session} onLogout={() => void handleLogout()} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
    </Routes>
  );
}
